/**
 * Discover Tab Component
 *
 * Matches original HTML structure: .welcome-section, .songs-grid container for song cards.
 */

import { isAuthenticated } from '@/stores/auth';
import { selectedGroup, canVote } from '@/stores/groups';
import { sortedSongs, sampleSongs, isLoading, fetchSampleSongs, votingProgress } from '@/stores/songs';
import { SongCard } from './SongCard';
import { SampleSongCard } from './SampleSongCard';
import { useEffect } from 'preact/hooks';

export function DiscoverTab() {
  // Fetch sample songs for unauthenticated users
  useEffect(() => {
    if (!isAuthenticated.value && sampleSongs.value.length === 0) {
      fetchSampleSongs();
    }
  }, []);

  // Show sample songs for unauthenticated users
  if (!isAuthenticated.value) {
    return <SampleSongsGrid />;
  }

  // Show loading state
  if (isLoading.value) {
    return <SongGridSkeleton />;
  }

  // No group selected
  if (!selectedGroup.value) {
    return (
      <div id="welcome-section" class="welcome-section" style={{ display: 'block' }}>
        <div class="welcome-content">
          <div class="brand-section">
            <img class="welcome-ghost-logo" src="/images/logo-transparent_nobuffer_crop_square.png" alt="Sherit" />
          </div>
          <p class="tagline">Select a Pod</p>
          <p class="description">Choose a pod from the bar above to see this month's songs</p>
        </div>
      </div>
    );
  }

  // No songs yet
  if (sortedSongs.value.length === 0) {
    return (
      <div id="welcome-section" class="welcome-section" style={{ display: 'block' }}>
        <div class="welcome-content">
          <div class="brand-section">
            <img class="welcome-ghost-logo" src="/images/logo-transparent_nobuffer_crop_square.png" alt="Sherit" />
          </div>
          <p class="tagline">No Songs Yet</p>
          <p class="description">Be the first to submit a song for {selectedGroup.value.currentMonth}!</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Section title - matches original */}
      <h2 class="section-title">
        {selectedGroup.value.currentMonth} {selectedGroup.value.currentYear}
        <span class="song-count">
          {' '}{sortedSongs.value.length} song{sortedSongs.value.length !== 1 ? 's' : ''}
        </span>
      </h2>

      {/* Voting progress bar */}
      {canVote.value && votingProgress.value.total > 0 && (
        <div class="voting-progress-container">
          <div class="voting-progress-bar">
            <div
              class="voting-progress-fill"
              style={{ width: `${votingProgress.value.percentage}%` }}
            />
          </div>
          <span class="voting-progress-text">
            {votingProgress.value.voted}/{votingProgress.value.total} voted
          </span>
        </div>
      )}

      {/* Songs grid - uses .songs-grid to match original CSS */}
      <div class="songs-grid">
        {sortedSongs.value.map((song) => (
          <SongCard key={song.id} song={song} />
        ))}
      </div>
    </>
  );
}

function SampleSongsGrid() {
  if (isLoading.value) {
    return <SongGridSkeleton />;
  }

  return (
    <>
      {/* Welcome section - matches original HTML structure */}
      {/* CSS has display:none by default, so we override with inline style */}
      <div id="welcome-section" class="welcome-section" style={{ display: 'block' }}>
        <div class="welcome-content">
          <div class="brand-section">
            <img class="welcome-ghost-logo" src="/images/logo-transparent_nobuffer_crop_square.png" alt="Sherit" />
          </div>
          <p class="tagline">Monthly Music Discovery, Community Driven</p>
          <p class="description">
            Join a community of music lovers discovering fresh tracks together. Every month, submit your favorite new song, vote on others' discoveries, and explore music you never knew you needed.
          </p>
        </div>
      </div>

      {/* No section title for sample songs - matches original */}
      <div class="songs-grid">
        {sampleSongs.value.map((song) => (
          <SampleSongCard key={song.id} song={song} />
        ))}
      </div>
    </>
  );
}

function SongGridSkeleton() {
  return (
    <>
      <h2 class="section-title">
        <span class="skeleton pulse" style={{ width: '150px', height: '24px', display: 'inline-block' }}></span>
      </h2>

      <div class="songs-grid">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div key={i} class="song-card skeleton">
            <div class="skeleton-image pulse"></div>
            <div class="skeleton-content">
              <div class="skeleton-title pulse"></div>
              <div class="skeleton-subtitle pulse"></div>
              <div class="skeleton-text pulse"></div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
